import type { ReactNode } from 'react'
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react'

type ContainerWidth = 'default' | 'wide' | 'adaptive' | 'full'

type PageLayoutMode = 'fill' | 'flow'

interface LayoutOptions {
  fullWidth?: boolean
  fullHeight?: boolean
  noHeaderSpacing?: boolean
  noPadding?: boolean
  containerWidth?: ContainerWidth
  headerActions?: ReactNode
  /**
   * 'fill' (default) keeps the historical nested-scroller layout: the page
   * column is height-constrained and each panel scrolls itself. 'flow' turns
   * the page into a single document scroller — the title and tabs stay pinned
   * above it, the filter bar pins itself to the top of it, and tables stop
   * owning their own viewport.
   */
  layout?: PageLayoutMode
}

interface LayoutContextType {
  layoutOptions: LayoutOptions
  setLayoutOptions: (options: LayoutOptions) => void
  updateLayoutOptions: (updates: Partial<LayoutOptions>) => void
}

const LayoutContext = createContext<LayoutContextType | undefined>(undefined)

export function LayoutProvider({ children }: { children: ReactNode }) {
  const [layoutOptions, setLayoutOptions] = useState<LayoutOptions>({})

  const updateLayoutOptions = useCallback((updates: Partial<LayoutOptions>) => {
    setLayoutOptions(prev => ({ ...prev, ...updates }))
  }, [])

  const value = useMemo(
    () => ({ layoutOptions, setLayoutOptions, updateLayoutOptions }),
    [layoutOptions, updateLayoutOptions],
  )

  return (
    <LayoutContext.Provider value={value}>{children}</LayoutContext.Provider>
  )
}

export function useLayout() {
  const context = useContext(LayoutContext)
  if (context === undefined) {
    // Return default values if context is not available
    return {
      layoutOptions: {},
      setLayoutOptions: () => {},
      updateLayoutOptions: () => {},
    }
  }
  return context
}

// Hook to set layout options with cleanup
export function useLayoutOptions(options: LayoutOptions) {
  const { updateLayoutOptions } = useLayout()

  useEffect(() => {
    // Apply the options when component mounts
    updateLayoutOptions(options)

    // Reset the options when component unmounts
    return () => {
      updateLayoutOptions({})
    }
  }, [JSON.stringify(options)]) // Use JSON.stringify to compare object values
}

export type { ContainerWidth, PageLayoutMode }
